import React, { useEffect, useState } from "react";
import { X, Loader2, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";

import {
  createMarketUpdateReport, getMarketUpdateTemplates, getSuburbInsights,
} from "../api/prospectingReportsApi";

const TEAL = "#55c5d0";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-AU")}`);

/**
 * Create a market update: pick a suburb and a template.
 *
 * The suburb's numbers are previewed BEFORE the report is generated, because a
 * suburb where Collings has no recorded sales produces a mostly-empty email and
 * the agent should find that out here rather than after sending it to 200
 * clients.
 */
const CreateMarketUpdateModal = ({ onClose, onCreated }) => {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ suburb: "", state: "VIC", postcode: "", template: "" });
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getMarketUpdateTemplates({ scope: "all" })
      .then(({ data }) => setTemplates(data?.results || []))
      .catch(() => setTemplates([]));
  }, []);

  useEffect(() => {
    if (!form.suburb.trim()) { setPreview(null); return undefined; }
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const { data } = await getSuburbInsights({
          suburb: form.suburb.trim(),
          state: form.state.trim(),
          postcode: form.postcode.trim(),
        });
        setPreview(data);
      } catch (error) {
        setPreview(null);
      } finally {
        setChecking(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.suburb, form.state, form.postcode]);

  const submit = async () => {
    if (!form.suburb.trim()) {
      toast.error("Enter a suburb.");
      return;
    }
    if (!form.template) {
      toast.error("Pick a template.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await createMarketUpdateReport({
        suburb: form.suburb.trim(),
        state: form.state.trim() || "VIC",
        postcode: form.postcode.trim(),
        template: Number(form.template),
      });
      toast.success("Market update created.");
      onCreated(data);
    } catch (error) {
      toast.error("Could not create the market update.");
    } finally {
      setSaving(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Create market update</h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Suburb</label>
              <input type="text" autoFocus {...field("suburb")} placeholder="Northcote"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">State</label>
              <input type="text" {...field("state")}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Template</label>
            <select {...field("template")}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
              <option value="">Choose a template</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.scope === "system" ? " (system)" : ""}
                </option>
              ))}
            </select>
            {templates.length === 0 && (
              <p className="text-[11px] text-gray-400 mt-1">
                No templates yet. Create one under Manage templates.
              </p>
            )}
          </div>

          {/* Suburb preview */}
          <div className="pt-2">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
              What this suburb's numbers look like
            </p>
            {checking ? (
              <div className="flex items-center gap-2 text-xs text-gray-400 py-3">
                <Loader2 className="w-4 h-4 animate-spin" /> Checking…
              </div>
            ) : !preview ? (
              <p className="text-xs text-gray-400 py-3">Enter a suburb to preview its statistics.</p>
            ) : !preview.has_data ? (
              <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  We have no statistics for {form.suburb}. Collings has no recorded sales here in the
                  window and Homer has no cached row, so the merge tags will come out blank.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["Median sale price", money(preview.headline.median_sale_price)],
                  ["Sold volume", preview.headline.sold_volume ?? "—"],
                  ["Price growth", preview.headline.price_growth_pct != null
                    ? `${preview.headline.price_growth_pct}%` : "—"],
                  ["Days on market", preview.headline.median_days_on_market ?? "—"],
                ].map(([label, value]) => (
                  <div key={label} className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
                    <p className="text-sm font-bold text-gray-900">{value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: TEAL }}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateMarketUpdateModal;
